import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useConfirm } from "@omit/react-confirm-dialog";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { MarketingWorkFile } from "@/bryntum-lib/models/MarketingWorkFile";
import { MarketingTag, MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import { MarketingWorkFileCard } from "@/bryntum-lib/models/MarketingWorkFileCard";
import type { CardFormState } from "../components/WorkFileLivePreview";

export interface WorkFilesData {
  workFiles: MarketingWorkFile[];
  filtered: MarketingWorkFile[];
  problemHubs: MarketingTag[];
  workfileCards: MarketingWorkFileCard[];
  query: string;
  setQuery: (q: string) => void;
  activeHubId: number | null;
  setActiveHubId: (id: number | null) => void;
  onArchiveFile: (file: MarketingWorkFile) => void;
  onUnarchiveFile: (file: MarketingWorkFile) => void;
  onDeleteFile: (file: MarketingWorkFile) => void;
}

export const useWorkFiles = (): WorkFilesData => {
  const { project } = useSwivuBryntumContext();
  const [query, setQuery] = useState("");
  const [activeHubId, setActiveHubId] = useState<number | null>(null);

  const { version: wfVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingWorkfileStore });
  const { version: tagVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingTagStore });
  const { version: cardVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingWorkfileCardStore });

  const workFiles = useMemo<MarketingWorkFile[]>(() => {
    const store = project?.getCrudStore(STORE_KEYS.marketingWorkfileStore);
    if (!store) return [];
    return store.query(() => true) as unknown as MarketingWorkFile[];
  }, [project, wfVersion]);

  const workfileCards = useMemo<MarketingWorkFileCard[]>(() => {
    const store = project?.getCrudStore(STORE_KEYS.marketingWorkfileCardStore);
    if (!store) return [];
    return store.query(() => true) as unknown as MarketingWorkFileCard[];
  }, [project, cardVersion]);

  const problemHubs = useMemo<MarketingTag[]>(() => {
    const store = project?.getCrudStore(STORE_KEYS.marketingTagStore);
    if (!store) return [];
    return (store.query(() => true) as unknown as MarketingTag[]).filter(
      (t) => t.type === MarketingTagType.PROBLEM
    );
  }, [project, tagVersion]);

  const filtered = useMemo<MarketingWorkFile[]>(() => {
    let list = workFiles;
    if (activeHubId !== null) {
      list = list.filter((f) => f.problemHubId === activeHubId);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((f) => f.title?.toLowerCase().includes(q));
    }
    return [...list].sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [workFiles, activeHubId, query]);

  const onArchiveFile = useCallback((file: MarketingWorkFile) => {
    file.archived = true;
  }, []);

  const onUnarchiveFile = useCallback((file: MarketingWorkFile) => {
    file.archived = false;
  }, []);

  const onDeleteFile = useCallback((file: MarketingWorkFile) => {
    (file as any).remove();
  }, []);

  return {
    workFiles,
    filtered,
    problemHubs,
    workfileCards,
    query,
    setQuery,
    activeHubId,
    setActiveHubId,
    onArchiveFile,
    onUnarchiveFile,
    onDeleteFile,
  };
};

// ---------------------------------------------------------------------------
// useWorkFileDetail
// Manages card selection, save, and delete logic for the detail view.
// ---------------------------------------------------------------------------

export interface WorkFileDetailData {
  fileCards: MarketingWorkFileCard[];
  selectedCardId: number | null;
  selectedCard: MarketingWorkFileCard | null;
  handleSelectCard: (id: number | null) => void;
  isAddingCard: boolean;
  handleAddCard: () => void;
  handleCancelAdd: () => void;
  handleSave: (data: CardFormState) => void;
  handleDelete: () => Promise<void>;
}

export const useWorkFileDetail = (
  file: MarketingWorkFile,
  cards: MarketingWorkFileCard[],
): WorkFileDetailData => {
  const confirm = useConfirm();
  const { project } = useSwivuBryntumContext();

  // pendingNewCard is an in-memory record created by handleAddCard but NOT yet added to the
  // store. It is prepended to fileCards so it appears as the first real list item. On save,
  // store.add() persists it; on cancel, it is simply discarded.
  const [pendingNewCard, setPendingNewCard] = useState<MarketingWorkFileCard | null>(null);
  // Saved so cancel can restore the card that was selected before "+ New" was clicked.
  const preAddSelectionRef = useRef<MarketingWorkFileCard | null>(null);
  // isAddingCard drives the form's UI mode (Cancel/Create buttons vs Delete/Save buttons).
  // Kept separate from pendingNewCard so we can flip the form to "edit" mode immediately on save
  // while pendingNewCard lingers until the store-derived `cards` prop catches up with the
  // 16ms-debounced version bump from useSwivuStore.
  const [isAddingCard, setIsAddingCard] = useState(false);

  const fileCards = useMemo(() => {
    const stored = cards
      .filter((c) => c.workFile === file.id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    // Dedupe: once `stored` includes pendingNewCard (after the version bump), drop the prepend
    // to avoid showing the same record twice. The same object reference appears in both, so
    // selection stays stable across the transition.
    return pendingNewCard && !stored.includes(pendingNewCard)
      ? [pendingNewCard, ...stored]
      : stored;
  }, [cards, file.id, pendingNewCard]);

  // Store the selected card by object reference — stable across Bryntum's temp→real ID mutation.
  const [selectedCard, setSelectedCard] = useState<MarketingWorkFileCard | null>(
    fileCards[0] ?? null,
  );

  // Auto-select first card when none is selected. Deletion is handled explicitly in handleDelete,
  // so we no longer fall back when selectedCard isn't in fileCards — that branch caused a flicker
  // during the 16ms gap between store.add() and the version bump.
  useEffect(() => {
    if (!selectedCard && fileCards[0]) {
      setSelectedCard(fileCards[0]);
    }
  }, [fileCards, selectedCard]);

  // Clear pendingNewCard once the store-derived `cards` prop reflects the addition.
  // Until then, fileCards continues to include pendingNewCard via the prepend, keeping the
  // selection highlight stable through the debounce gap.
  useEffect(() => {
    if (pendingNewCard && cards.includes(pendingNewCard)) {
      setPendingNewCard(null);
    }
  }, [cards, pendingNewCard]);

  const selectedCardId = selectedCard?.id ?? null;

  const handleSelectCard = useCallback(
    (id: number | null) => {
      setSelectedCard(id != null ? (fileCards.find((c) => c.id === id) ?? null) : null);
    },
    [fileCards],
  );

  const handleAddCard = useCallback(() => {
    // If a pending card already exists, just switch back to it instead of creating another.
    if (pendingNewCard) {
      setSelectedCard(pendingNewCard);
      return;
    }
    const store = project?.getCrudStore(STORE_KEYS.marketingWorkfileCardStore);
    if (!store) return;
    preAddSelectionRef.current = selectedCard;
    const newRecord = (store as any).createRecord({ workFile: file.id, order: -1 });
    setPendingNewCard(newRecord as MarketingWorkFileCard);
    setSelectedCard(newRecord as MarketingWorkFileCard);
    setIsAddingCard(true);
  }, [project, file.id, selectedCard, pendingNewCard]);

  const handleCancelAdd = useCallback(() => {
    // Restore the card that was selected before the user clicked "+ New", falling
    // back to the first stored card if nothing was previously selected.
    setSelectedCard(preAddSelectionRef.current ?? fileCards.find((c) => c !== pendingNewCard) ?? null);
    preAddSelectionRef.current = null;
    setPendingNewCard(null);
    setIsAddingCard(false);
  }, [fileCards, pendingNewCard]);

  const handleSave = useCallback(
    (data: CardFormState) => {
      if (pendingNewCard) {
        const store = project?.getCrudStore(STORE_KEYS.marketingWorkfileCardStore);
        if (!store) return;
        (pendingNewCard as any).set(data);
        store.add(pendingNewCard);
        setIsAddingCard(false);
        // Do NOT clear pendingNewCard here — the effect above clears it once the store-derived
        // `cards` prop reflects the addition. This avoids the 16ms debounce gap in useSwivuStore
        // that would otherwise drop the pending record from fileCards mid-render and trigger
        // an unwanted selection switch.
      } else {
        if (!selectedCard) return;
        (selectedCard as any).set(data);
      }
    },
    [pendingNewCard, project, selectedCard],
  );

  const handleDelete = useCallback(async () => {
    if (!selectedCard) return;
    const confirmed = await confirm({
      title: "Delete card",
      description: `Delete "${selectedCard.heading || "Untitled card"}"? This action cannot be undone.`,
    });
    if (!confirmed) return;

    const idx = fileCards.findIndex((c) => c === selectedCard);
    const next = fileCards[idx + 1] ?? fileCards[idx - 1] ?? null;
    (selectedCard as any).remove();
    setSelectedCard(next);
  }, [confirm, fileCards, selectedCard]);

  return {
    fileCards,
    selectedCardId,
    selectedCard,
    handleSelectCard,
    isAddingCard,
    handleAddCard,
    handleCancelAdd,
    handleSave,
    handleDelete,
  };
};
